import React, { useMemo, useState, useContext, useEffect } from 'react';
import { useUrlDateType } from '../Hooks/useUrlDateType';
import { useUrlSorting } from '../Hooks/useUrlSorting';
import { useUrlFilters, createFieldConfig, createDateConfig } from '../Hooks/useUrlFilters';
import { useViewFilters, usePaginationReset } from '../Hooks/useViewFilters';
import { useViewQuery } from '../Hooks/useViewQuery';
import { calculateRowCount } from '../utils/viewUtils';
import { useReusableTable } from '../common/TablePanel/tablePanel';
import { MaterialReactTable } from 'material-react-table';
import { TimezoneContext } from '../common/TimezoneContext/TimezoneContext';
import { Box, ThemeProvider } from '@mui/material';
import FiltersComponent from '../common/FiltersPanel/FIltersPanel';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouterIcon from '@mui/icons-material/Router';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FolderIcon from '@mui/icons-material/Folder';
import FilterDropdown from '../common/FilterDropdown/FilterDropdown';
import './DITView.css';

const PREF_STORAGE_KEY = 'dit_filter_preferences';
const DEFAULT_FIELDS = {
  vin: [],
  mac_id: [],
  vescom: [],
  year: [],
  program: [],
};

const filterConfig = {
    dateFields: [
    createDateConfig('start_datetime', 'end_datetime', {
      startParam: 'start_date',
      endParam: 'end_date',
      allowEmpty: true,
      defaultStart: null,
      defaultEnd: null,
    }),
  ],
  debounceDelay: 150,
};

const DITApp = ({ activeTheme }) => {
  const [dateType, handleDateTypeChange] = useUrlDateType();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 100 });
  const [sorting, setSorting] = useUrlSorting([{ id: 'datetime', desc: true }]);

  const mapping = { vin: 'vin', mac_id: 'mac_id', vescom: 'vescom', year: 'year', program: 'program' };

  const {
    singleValueFilters,
    debouncedSingleValueFilters,
    handleSingleFilterChange,
    handleSavePreferences,
    getOtherFilters,
    mergeField,
  } = useViewFilters({
    storageKey: PREF_STORAGE_KEY,
    defaultFields: DEFAULT_FIELDS,
    urlMapping: mapping,
    debounceDelay: 1000,
  });

  const { filters, debouncedFilters, handleDateRangeChange } = useUrlFilters(filterConfig);
  const { formatDateTime, createIsoStringWithTimezone, userTimezone, handleTimezoneChange } = useContext(TimezoneContext);

  const effectiveTimezone = userTimezone;

  const mergedFilters = useMemo(() => {
    return {
      ...debouncedFilters,
      vin: mergeField(debouncedSingleValueFilters.vin, debouncedFilters.vin),
      mac_id: mergeField(debouncedSingleValueFilters.mac_id, debouncedFilters.mac_id),
      vescom: mergeField(debouncedSingleValueFilters.vescom, debouncedFilters.vescom),
      year: mergeField(debouncedSingleValueFilters.year, debouncedFilters.year),
      program: mergeField(debouncedSingleValueFilters.program, debouncedFilters.program),
    };
  }, [debouncedFilters, debouncedSingleValueFilters, mergeField]);

  usePaginationReset(mergedFilters, dateType, setPagination);

  const ditQuery = useViewQuery({
    queryKey: 'dit-data',
    mergedFilters,
    pagination,
    sorting,
    dateType,
    urlMapping: mapping,
    createIsoStringWithTimezone,
    endpoint: 'api/v1/dit/query',
  });

  const data = useMemo(() => ditQuery.data?.results || [], [ditQuery.data]);
  const rowCount = useMemo(() => calculateRowCount(ditQuery.data, data, pagination), [ditQuery.data, data, pagination]);

  const columns = useMemo(() => [
    { accessorKey: 'vescom', header: 'Vescom' },
    { accessorKey: 'program', header: 'Program' },
    { accessorKey: 'year', header: 'Year' },
    { accessorKey: 'date', header: 'Created on Device', Cell: ({ cell }) => formatDateTime(cell.getValue(), 'YYYY-MM-DD HH:mm:ss') },
    { accessorKey: 'datetime_uploaded', header: 'Uploaded to Cloud', Cell: ({ cell }) => formatDateTime(cell.getValue(), 'YYYY-MM-DD HH:mm:ss') },
    { accessorKey: 'filename', header: 'Filename' },
    { accessorKey: 'vin', header: 'VIN' },
    { accessorKey: 'mac_id', header: 'MAC ID' },
  ], [formatDateTime]);

  const dropdowns = useMemo(() => (
    <>
      <Box sx={{ mt: 2 }}>
        <FilterDropdown
          field="vescom"
          label="Vescom"
          value={singleValueFilters.vescom}
          onChange={(value) => handleSingleFilterChange('vescom', value)}
          apiBaseUrl="/api/dit"
          multiple={true}
          icon={SettingsIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('vescom')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="year"
          label="Year"
          value={singleValueFilters.year}
          onChange={(value) => handleSingleFilterChange('year', value)}
          apiBaseUrl="/api/dit"
          multiple={true}
          icon={CalendarTodayIcon}
          disableSearch={true}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('year')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="program"
          label="Program"
          value={singleValueFilters.program}
          onChange={(value) => handleSingleFilterChange('program', value)}
          apiBaseUrl="/api/dit"
          multiple={true}
          icon={FolderIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('program')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="vin"
          label="VIN"
          value={singleValueFilters.vin}
          onChange={(value) => handleSingleFilterChange('vin', value)}
          apiBaseUrl="/api/dit"
          multiple={true}
          icon={DirectionsCarIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('vin')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="mac_id"
          label="MAC ID"
          value={singleValueFilters.mac_id}
          onChange={(value) => handleSingleFilterChange('mac_id', value)}
          apiBaseUrl="/api/dit"
          multiple={true}
          icon={RouterIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('mac_id')}
          fieldMapping={mapping}
        />
      </Box>
    </>
  ), [
    singleValueFilters,
    handleSingleFilterChange,
    debouncedFilters,
    dateType,
    createIsoStringWithTimezone,
    getOtherFilters,
    mapping
  ]);

  const { table, setRowSelection } = useReusableTable({
    columns,
    data,
    rowCount: rowCount,
    isLoading: ditQuery.isFetching,
    enableRowSelection: true,
    enableRowExpansion: false,
    enableCompareFiles: false,
    enableRowVirtualization: true,
    initialPageSize: 100,
    maxHeight: 'calc(100vh - 250px)',
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 250px)' } },
    enableDefaultPreview: true,
    enableDefaultDownload: true,
    enableDefaultAddToCart: true,
    getRowId: (row) => row.id ?? `${row.vin}-${row.datetime_uploaded}-${row.filename}`,
    pagination,
    sorting,
    onSortingChange: setSorting,
    manualPagination: true,
    onPaginationChange: setPagination,
    paginationDisplayMode: 'default',
    muiPaginationProps: { 
      color: 'primary', 
      rowsPerPageOptions: [100, 200, 500, 1000],
      showFirstButton: false,
      showLastButton: false
    },
  });

  useEffect(() => {
    setRowSelection({});
  }, [mergedFilters, pagination.pageIndex, setRowSelection]);


  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="dit-main-container">
        <Box>
          <FiltersComponent
            activeTheme={activeTheme}
            dateRangePicker={{ filters: filters || {}, handleDateRangeChange }}
            timezoneSelector={{ timezone: effectiveTimezone, userTimezone, handleTimezoneChange }}
            dateTypeSelector={{ dateType, handleDateTypeChange }}
            onSaveSearch={handleSavePreferences}
            storageKey={PREF_STORAGE_KEY}
            dropdowns={dropdowns}
          />
        </Box>
        <Box className="dit-content">
          <MaterialReactTable table={table} />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DITApp;